// Public embed configuration only. Private API credentials never belong here.
import { AGENTPLANE_ORIGIN } from './support-origin';
import { createPushBridge } from './support-push';
export { AGENTPLANE_ORIGIN };

export function supportConfig(data = {}, platform = 'web') {
    data = data || {};
    const provider = data.support_provider || (data.crisp_website_id ? 'crisp' : 'disabled');
    const mode = ['bubble', 'inline', 'both'].includes(data.agentplane_display_mode) ? data.agentplane_display_mode : 'both';
    const configured = provider === 'agentplane' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.agentplane_agent_id || '') && /^ap_[a-zA-Z0-9]+$/.test(data.agentplane_embed_key || '');
    return {
        provider,
        crisp: provider === 'crisp' && !!data.crisp_website_id,
        configured,
        bubble: configured && mode !== 'inline',
        inline: configured && mode !== 'bubble',
        agentId: data.agentplane_agent_id || '',
        key: data.agentplane_embed_key || '',
        pushEnabled: platform === 'web' && data.agentplane_push_enabled !== false && data.agentplane_push_enabled !== 0,
        pushPublicKey: data.agentplane_push_public_key || '',
    };
}

// Shared across route mounts, with one live frame at a time. Every new browser
// lifecycle clears stale ownership once; subsequent mounts and refreshes resume it.
const owners = new Map();
let activeFrame;
export function createAgentplaneFrame({ host, frame, config, user, visible, onReady, onError, onClose, onUnread, onOpen = () => {}, onPushStatus = () => {}, onReset = () => {}, onRestart = () => {} }) {
    activeFrame = frame;
    let disposed = false;
    let ready = false;
    let currentUser = user;
    let isVisible = visible;
    let sentIdentity;
    let resetPhase;
    let timer;
    const ownerKey = config.agentId + ':' + host.location.origin;
    const storageKey = 'derasa-support-owner:' + config.agentId;
    let owner = owners.get(ownerKey);
    try {
        const stored = JSON.parse(host.localStorage.getItem(storageKey));
        if (stored && (stored.id === null || typeof stored.id === 'string')) owner = stored;
    } catch (error) { /* Storage can be blocked; use this page's in-memory owner. */ }
    function post(message) {
        if (!disposed && frame.contentWindow) frame.contentWindow.postMessage(message, AGENTPLANE_ORIGIN);
    }
    const push = createPushBridge({ host, config, post, owner: ownerKey + ':' + (user && user.id != null ? user.id : 'guest'), onStatus: onPushStatus });
    function identityId(value) {
        return value && value.id != null ? String(value.id) : null;
    }
    function saveOwner() {
        owners.set(ownerKey, owner);
        try { host.localStorage.setItem(storageKey, JSON.stringify(owner)); } catch (error) { /* optional storage */ }
    }
    function armTimeout() {
        host.clearTimeout(timer);
        timer = host.setTimeout(() => { if (!ready && !disposed) onError(); }, 15000);
    }
    function sync() {
        if (!ready) return;
        const id = identityId(currentUser);
        if (!owner || owner.id !== id || owner.needsCleanFrame !== false) {
            push.changeOwner(ownerKey + ':' + (id === null ? 'guest' : id));
            // A hosted iframe can have an in-flight restore of the previous
            // identity. Clear storage, discard that document, then reset the
            // fresh frame before identifying or exposing any conversation.
            resetPhase = owner && owner.id === id && owner.needsCleanFrame ? 'finish' : 'restart';
            owner = { id, needsCleanFrame: true };
            saveOwner();
            ready = false;
            sentIdentity = undefined;
            onReset();
            armTimeout();
            post({ type: 'agentplane:reset' });
            return;
        }
        if (id !== null) {
            const payload = {
                // Namespace identities by the host app, since multiple generic
                // installations can have the same numeric student ID.
                userId: host.location.origin + ':' + id,
                name: currentUser.name || undefined,
                email: currentUser.email || undefined,
                phone: currentUser.phone || undefined,
                attributes: { drasa_student_id: id, app_url: host.location.origin },
            };
            const fingerprint = JSON.stringify(payload);
            if (fingerprint !== sentIdentity) {
                post({ type: 'agentplane:identify', payload });
                sentIdentity = fingerprint;
            }
        }
        post({ type: 'agentplane:visible', open: isVisible && !host.document.hidden });
    }
    function page() {
        if (ready) post({ type: 'agentplane:page', url: host.location.href, title: host.document.title });
    }
    armTimeout();
    function message(event) {
        if (disposed || activeFrame !== frame || event.origin !== AGENTPLANE_ORIGIN || event.source !== frame.contentWindow || !event.data) return;
        switch (event.data.type) {
        case 'agentplane:ready':
            if (resetPhase === 'restart') {
                host.clearTimeout(timer);
                onRestart();
                return;
            }
            if (resetPhase === 'finish') {
                owner.needsCleanFrame = false;
                saveOwner();
                resetPhase = null;
            }
            ready = true;
            // The widget can announce a new session without reloading its iframe.
            sentIdentity = undefined;
            host.clearTimeout(timer);
            sync();
            if (!ready) return;
            page();
            push.ready();
            onReady();
            break;
        case 'agentplane:close': onClose(); break;
        case 'agentplane:open': onOpen(); break;
        case 'agentplane:push-request': if (ready) push.enable(); break;
        case 'agentplane:unread': if (ready && (!isVisible || host.document.hidden)) onUnread(); break;
        }
    }
    host.addEventListener('message', message);
    const url = new URL('/embed/' + config.agentId, AGENTPLANE_ORIGIN);
    url.searchParams.set('key', config.key);
    url.searchParams.set('display', 'inline');
    frame.src = url.toString();
    return {
        updateUser(value) { currentUser = value; sync(); },
        setVisible(value) { isVisible = value; sync(); },
        page,
        enablePush() { if (ready) return push.enable(); },
        dispose() {
            disposed = true;
            if (activeFrame === frame) activeFrame = null;
            push.dispose();
            host.clearTimeout(timer);
            host.removeEventListener('message', message);
        },
    };
}
